import { useEffect, useState } from "react";
import "./AdminDashboard.css";
import { BiMoney } from "react-icons/bi";
import { Link, useNavigate } from "react-router-dom";
import { ResizableBox as ReactResizableBox } from "react-resizable";
import ChartGraph from "./chart/ChartGraph";
import JobSummary from "../../components/jobSummary/JobSummary";
import {
  useGetlocumAnnualSummaryQuery,
  useGetlocumCardsQuery,
  useGetlocumDivisionSummaryQuery,
} from "../../store/query/locum";
import { useTypedSelector } from "../../hook/useTypedSelector";
import { _currency_format, _figure_format } from "../../utils";
import CustomAutoComplete from "../../components/customAutoComplete/CustomAutoComplete";
import emptyIcon from "../../assets/empty.png";
import { config } from "../../config";
import { useGetJobsByStatusQuery } from "../../store/query/dashboard";
import { MdOutlineKeyboardArrowRight } from "react-icons/md";

const AdminDashboard = () => {
  const { user } = useTypedSelector((store) => store.appUser);
  const navigate = useNavigate();

  const [filters, setFilters] = useState({
    pay_type: user.account_type === "organisation" ? "payout" : "payin",
    month: "",
    year: "",
  });

  const { data: cards } = useGetlocumCardsQuery({
    acc_type: user?.account_type,
  });

  const { data: summary } = useGetlocumDivisionSummaryQuery({
    acc_type: user?.account_type,
  });

  const { data: annual } = useGetlocumAnnualSummaryQuery({
    acc_type: user?.account_type,
  });

  const { data: openJobs, isLoading: isLoadingOpenJobs } =
    useGetJobsByStatusQuery({
      acc_type: user?.account_type,
      type: "open",
    });

  const months = Array.from({ length: 12 }, (_, i) => ({
    label: new Date(0, i).toLocaleString("en-US", { month: "long" }),
    value: (i + 1).toString().padStart(2, "0"),
  }));
  const currentMonthValue = new Date().getMonth() + 1; // Get current month (1-12)
  const currentMonth = months.find(
    (month) => month.value === currentMonthValue.toString().padStart(2, "0")
  );
  //console.log("open", cards);

  useEffect(() => {
    setFilters({
      pay_type: user.account_type === "organisation" ? "payout" : "payin",
      month: currentMonth?.value,
      year: "2025",
    });
  }, [currentMonth?.value, user?.account_type]);

  return (
    <div className="admin-dashboard-container">
      <div className="admin-dashboard-right">
        <div className="admin-dashboard-right-bottom">
          <div className="admin-info-box">
            <Link className="admin-info-right one" to="/postedjobs">
              <div>
                <div>
                  <span>My Published Jobs</span>
                </div>
                <h4>
                  {_figure_format(Number(cards?.data[0]?.publishedJob ?? 0))}
                </h4>
              </div>

              <MdOutlineKeyboardArrowRight size={24} />
            </Link>

            <div className="admin-info-right two">
              <div>
                <span>Total Applicants</span>
                <h4>
                  {_figure_format(Number(cards?.data[0]?.totalApplicant ?? 0))}
                </h4>
              </div>
            </div>

            <Link className="admin-info-right three">
              <div>
                <span>Total Payout</span>
                <h4>
                  {_currency_format(
                    Number(cards?.data[0]?.totalPayout ?? 0),
                    "NGN"
                  )}
                </h4>
              </div>
            </Link>
          </div>

          <div className="graph-container">
            <div className="graph-box">
              <div className="graph-header">
                <h6>
                  {filters.pay_type === "payout"
                    ? "Daily Payout Statistics"
                    : "Daily Earning Statistics"}
                </h6>

                <div className="input-body">
                  {user?.account_type === "individual" && (
                    <div>
                      <CustomAutoComplete
                        isClearable={false}
                        initOption={
                          filters.year.length
                            ? filters.year
                            : {
                                label: "Earning",
                                value: "payin",
                              }
                        }
                        options={[
                          {
                            label: "Earning",
                            value: "payin",
                          },
                          {
                            label: "Payout",
                            value: "payout",
                          },
                        ]}
                        customStyles={{
                          control: {
                            width: "100%",
                            borderRadius: "0",
                          },
                          valueContainer: {
                            height: "40px",
                          },
                        }}
                        onChange={(option) => {
                          setFilters((prev) => ({
                            ...prev,
                            pay_type: option?.value,
                          }));
                        }}
                      />
                    </div>
                  )}
                  <div>
                    <CustomAutoComplete
                      isClearable={false}
                      placeholder={"Month"}
                      options={months}
                      initOption={
                        filters.month.length ? filters.month : currentMonth
                      }
                      customStyles={{
                        control: {
                          width: "100%",
                          borderRadius: "0",
                        },
                        valueContainer: {
                          height: "40px",
                        },
                      }}
                      onChange={(option) => {
                        setFilters((prev) => ({
                          ...prev,
                          month: option?.value,
                        }));
                      }}
                    />
                  </div>
                  <div>
                    <CustomAutoComplete
                      isClearable={false}
                      initOption={
                        filters.year.length
                          ? filters.year
                          : {
                              label: "2025",
                              value: "2025",
                            }
                      }
                      placeholder={"Year"}
                      options={[
                        {
                          label: "2025",
                          value: "2025",
                        },
                        {
                          label: "2026",
                          value: "2026",
                        },
                        {
                          label: "2027",
                          value: "2027",
                        },
                      ]}
                      customStyles={{
                        control: {
                          width: "100%",
                          borderRadius: "0",
                        },
                        valueContainer: {
                          height: "40px",
                        },
                      }}
                      onChange={(option) => {
                        setFilters((prev) => ({
                          ...prev,
                          year: option?.value,
                        }));
                      }}
                    />
                  </div>
                </div>
              </div>
              <ReactResizableBox
                width={"100%"}
                height={"500"}
                className="chart-bg"
              >
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                  }}
                >
                  <ChartGraph filters={filters} />
                </div>
              </ReactResizableBox>
            </div>
            <div className="summary-box">
              <h6>Payout Summary Per Division</h6>

              <div className="contain-summary">
                {summary?.data.length > 0 ? (
                  summary?.data?.map((item, index) => {
                    const [firstKey] = Object.keys(item);
                    const firstValue = item[firstKey];
                    const totalPayout =
                      Number(cards?.data[0]?.totalPayout) || 1;

                    const percentage = (
                      (Number(firstValue) / totalPayout) *
                      100
                    ).toFixed(2); // calcul

                    return (
                      <JobSummary
                        key={index}
                        firstKey={firstKey}
                        per={percentage}
                        amount={firstValue}
                      />
                    );
                  })
                ) : (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 10,
                      alignItems: "center",
                      justifyContent: "center",
                      minHeight: "200px",
                    }}
                  >
                    <img src={emptyIcon} alt="empty record" className="" />
                    <p className="">No Summary found</p>
                  </div>
                )}
              </div>

              <div className="cards-total">
                {user?.account_type === "individual" && (
                  <div className="revenue-total">
                    <BiMoney />
                    <div className="revenue-context">
                      <h4>
                        &#8358;
                        {_figure_format(
                          Number(cards?.data[0]?.totalPayin ?? 0)
                        )}
                      </h4>
                      <p>Total Earnings Per Annum</p>
                    </div>
                  </div>
                )}

                <div className="revenue-total">
                  <BiMoney />
                  <div className="revenue-context">
                    <h4>
                      &#8358;{" "}
                      {_figure_format(
                        Number(annual?.data[0]?.totalYearPayout ?? 0)
                      )}
                    </h4>
                    <p>Total Payout Per Annum</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="admin-openJobs">
            <h4>My Open Jobs</h4>

            <div className="admin-openJobs-box">
              {openJobs?.OpenJobs?.map((job) => {
                const logo =
                  job.logo && `${config.getPublicImages}/${job.logo}`;
                return (
                  <div key={job.id} className="single-job-box">
                    <div className="single-title-box">
                      <div className="title">
                        <h4>
                          {job.title.length > 12
                            ? job.title.slice(0, 15) + "..."
                            : job.title}
                        </h4>
                        <span>Rate:N{job.hourly_rate}/hr</span>
                      </div>
                      <div className="image">
                        <img src={logo} alt="img" />
                      </div>
                    </div>
                    <div className="desc">
                      <p>
                        {job.description.length > 100
                          ? job.description.slice(0, 100) + "..."
                          : job.description}
                      </p>
                    </div>
                    <div className="applicants">
                      <h6>
                        {job.applicants <= 1
                          ? `${job.applicants ?? 0} Applicant`
                          : `${job.applicants ?? 0} Applicants`}
                      </h6>

                      <span
                        onClick={() =>
                          navigate(`/joblistings/${job.id}/posted`)
                        }
                      >
                        View
                      </span>
                    </div>
                  </div>
                );
              })}

              {isLoadingOpenJobs && (
                <div style={{ marginTop: "30px", marginLeft: "5px" }}>
                  <p>Loading...</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
